import React, { useState, useRef } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { noAutofillProps } from '../../utils/noAutofill';

const CityAutocomplete = ({ value, onChange, placeholder, className, types = ['(cities)'], onPlaceSelect }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const searchCities = async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use the backend proxy endpoint for Google Places API
      const params = { query };
      const typesStr = types.join(',');
      if (typesStr) params.types = typesStr;

      const response = await axios.get(`${getApiBase()}/places/autocomplete`, {
        params,
        headers: getAuthHeaders()
      });

      if (response.data.status === 'OK' && response.data.predictions) {
        const cityResults = response.data.predictions.map(prediction => ({
          place_id: prediction.place_id,
          description: prediction.description,
          structured_formatting: prediction.structured_formatting,
          terms: prediction.terms
        }));
        setSuggestions(cityResults.slice(0, 8));
      } else if (response.data.status === 'ZERO_RESULTS') {
        setSuggestions([]);
      } else {
        console.warn('Google Places API returned status:', response.data.status);
        setError('Unable to fetch city suggestions');
        setSuggestions([]);
      }
    } catch (err) {
      console.error('Error fetching city suggestions:', err);
      if (err.response?.status === 503) {
        setError('Google Places API is not configured');
      } else {
        setError('Failed to fetch city suggestions');
      }
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);
    setShowSuggestions(true);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      searchCities(inputValue);
    }, 300);
  };

  const handleSelectCity = (place) => {
    const displayValue = place.structured_formatting?.main_text || place.description;
    onChange(displayValue);
    setShowSuggestions(false);
    setSuggestions([]);

    // If onPlaceSelect is provided, call it with the full place object
    if (onPlaceSelect) {
      const placeObject = {
        place_id: place.place_id,
        name: place.structured_formatting?.main_text || '',
        formatted_address: place.description,
        geometry: place.geometry || {},
        types: place.types || []
      };
      onPlaceSelect(placeObject);
    }
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const handleFocus = () => {
    setShowSuggestions(true);
    if (value && value.length >= 2) {
      searchCities(value);
    }
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        {...noAutofillProps}
        name="city-search"
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={className}
      />

      {error && !loading && (
        <div className="absolute z-10 w-full rounded-md shadow-lg mt-1 p-2 bg-ui-amber-bg border border-ui-amber-line">
          <div className="text-xs text-ui-amber-fg">
            ⚠️ {error}
          </div>
        </div>
      )}

      {showSuggestions && (suggestions.length > 0 || loading) && (
        <div className="absolute z-10 w-full rounded-xl shadow-lg max-h-64 overflow-y-auto mt-1 bg-ui-surface border border-ui-control">
          {loading && (
            <div className="px-3 py-2 text-sm text-ui-muted">
              🔍 Searching...
            </div>
          )}

          {!loading && suggestions.map((city) => (
            <button
              key={city.place_id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelectCity(city)}
              className="w-full text-left px-3 py-2 focus:outline-none border-b last:border-b-0 hover:bg-ui-hover focus:bg-ui-hover border-ui-divider"
            >
              <div className="font-medium text-ui-ink">
                {city.structured_formatting?.main_text}
              </div>
              <div className="text-sm text-ui-muted">
                {city.structured_formatting?.secondary_text}
              </div>
            </button>
          ))}

          {!loading && suggestions.length === 0 && value && value.length >= 2 && !error && (
            <div className="px-3 py-2 text-sm text-ui-muted">
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CityAutocomplete;
